/**
 * Общи помощни функции за печатните изгледи — един документ
 * (PrintDocument.jsx) или обединена фактура от няколко документа
 * (PrintInvoice.jsx). Изнесени тук, за да не се дублира логиката, която
 * решава какво точно пише на хартия.
 */

const round2 = (x) => Math.round(x * 100) / 100;

/**
 * Дата + брой дни, без да минава през toISOString() (той мести с ден
 * заради UTC — виж бележката в api.js за isoDate()).
 */
export function dobaviDni(isoDate, days) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d + Number(days || 0));
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

/** Показва ставката, ако е една за целия документ. */
export function vatRateOf(items) {
  const rates = [...new Set(
    items
      .filter((i) => !Number(i.is_cancelled) && Number(i.line_subtotal) !== 0)
      .map((i) => Number(i.vat_rate)))];
  return rates.length === 1 ? rates[0] : '';
}

/**
 * Какво пише в колоната „Наименование".
 *
 * Ръчната поправка (`print_description`) печели винаги — операторът я е
 * задал нарочно, точно защото клиент не иска нещо конкретно да се вижда.
 * Иначе: каталожното име е вътрешен код за търсене (напр.
 * „100HPCE505X/CF280X/719"), не описание — вместо него излиза бележката
 * на стоката („каква е стоката" в картона ѝ) плюс капацитета, ако има
 * такъв. Ако и двете липсват, пада към каталожното име — фактурата не
 * може да излезе с празен ред, наредбата го изисква.
 */
export function opisanieNaReda(it) {
  if (it.print_description) return it.print_description;
  const parts = [
    it.product_note,
    it.product_capacity ? `Капацитет ${it.product_capacity}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : it.description;
}

/**
 * На печат редовете по партиди на една и съща стока и цена се сливат.
 *
 * Партидата е вътрешна подялба за себестойността — клиентът не я вижда и
 * не го интересува от коя точно е взето. Два реда с идентична стока,
 * мярка, ставка и единична цена на печат изглеждат като едно и също нещо
 * купено два пъти; на хартия трябва да е един ред с общото количество.
 *
 * Различна цена или бележка на реда НЕ се сливат — това вече е различно
 * решение на оператора, не подялба по партиди, и трябва да си личи.
 * Обединена фактура слива и през границата на отделните документи —
 * клиентът вижда стоката, не от кой протокол точно е дошла.
 */
export function slivaniRedove(items) {
  const grupi = [];
  const poKlyuch = new Map();
  for (const it of items) {
    const kol = Number(it.quantity || 0);
    const osnova = Number(it.line_subtotal || 0);
    // Единичната цена, закръглена както се показва — две мерки с различна
    // видима цена не бива да минат за една само защото делението недоокръгля.
    const cenaZaRed = kol ? (Math.round((osnova / kol) * 10000) / 10000).toFixed(4)
      : Number(it.price || 0).toFixed(4);
    const klyuch = [
      it.product_id ?? `text:${it.description}`, it.unit, it.vat_rate,
      it.is_gift ? 1 : 0, it.note ?? '', cenaZaRed,
    ].join('|');

    const stig = poKlyuch.get(klyuch);
    if (!stig) {
      const nov = { ...it };
      poKlyuch.set(klyuch, nov);
      grupi.push(nov);
      continue;
    }
    stig.quantity = kol + Number(stig.quantity || 0);
    stig.line_subtotal = round2(osnova + Number(stig.line_subtotal || 0));
    stig.line_vat = round2(Number(it.line_vat || 0) + Number(stig.line_vat || 0));
    stig.line_total = round2(Number(it.line_total || 0) + Number(stig.line_total || 0));
    // Партидата вече не е една — не се показва коя точно.
    if (stig.batch_no !== it.batch_no) stig.batch_no = null;
  }
  return grupi;
}
